/**
 * EL MRZ COMO ANCLA del dorso.
 *
 * El equivalente del código de barras para la otra cara. El MRZ es el bloque más
 * reconocible del documento: tres renglones largos, paralelos, monoespaciados y
 * llenos de '<'. Nada más en la tarjeta se parece a eso.
 *
 * Sirve para lo mismo: da el ÁNGULO de la foto y dice dónde está la tarjeta,
 * incluso cuando el texto sale mal leído. Y eso importa porque el ángulo se
 * puede sacar de las CAJAS de las palabras aunque los caracteres estén
 * equivocados: la geometría sobrevive a la mala lectura.
 */
import { PalabraOcr } from "../dniMatch";
import { Rectangulo } from "./types";
import { TARJETA_ID1 } from "./tuning";

/** Cuánto se parece este texto a una línea de MRZ. 0 a 1. */
/**
 * Dónde empieza el bloque del MRZ, como fracción del alto de la tarjeta.
 *
 * MEDIDO sobre dos dorsos completos: el primer renglón arranca a 0,709 del alto
 * en el modelo 2012 y a 0,690 en el 2026.
 *
 * El valor estimado anterior era 0,59, y esos 11 puntos de diferencia ubicaban
 * el borde de arriba de la tarjeta más abajo de donde está: el recorte empezaba
 * dentro del documento y se comía la línea del domicilio.
 */
const ARRANQUE_DEL_MRZ = 0.7;

export function densidadDeMrz(texto: string): number {
  const t = texto.toUpperCase().replace(/\s+/g, "");
  if (t.length < 8) return 0;

  // Tiene que haber al menos un '<' de verdad. Sin esta condición, contar K, L,
  // S e Y como relleno probable hacía que "SAN MARTIN 2538 CANUELAS" puntuara
  // 0,57 y pasara por MRZ: son letras comunes del español.
  const rellenoReal = (t.match(/[<«‹]/g) ?? []).length;
  if (rellenoReal === 0) return 0;

  // Ya sabiendo que hay relleno, las letras que el OCR suele poner en su lugar
  // cuentan también: es lo que permite reconocer una línea mal leída.
  const relleno = rellenoReal + (t.match(/[KLSY]/g) ?? []).length;
  const validos = (t.match(/[A-Z0-9<]/g) ?? []).length;
  return (validos / t.length) * Math.min(1, relleno / (t.length * 0.25));
}

export interface OrientacionPorMrz {
  anguloGrados: number;
  cuartosDeVuelta: 0 | 90 | 180 | 270;
  /** Las cajas de los renglones que se tomaron por MRZ. */
  renglones: Rectangulo[];
  /** La tarjeta deducida, si el MRZ ocupa el ancho que se espera. */
  tarjeta: Rectangulo | null;
}

/**
 * Ángulo del dorso a partir del MRZ.
 *
 * Se buscan los renglones con más pinta de MRZ y se ajusta una recta por los
 * centros de sus palabras. Con dos o tres renglones paralelos la recta es
 * estable aunque el OCR haya leído cualquier cosa.
 */
export function orientacionPorMrz(
  palabras: PalabraOcr[],
  fraccionAnchoMrz = 0.87,
  /**
   * Cuántos tokens con pinta de MRZ hacen falta. Tres es un MRZ entero, y es lo
   * que se pide cuando de esto depende un recorte automático.
   *
   * Se puede bajar a dos donde equivocarse sea barato. Medido sobre una foto
   * con las dos caras: el OCR devolvió las líneas 2 y 3 y perdió la 1, y con
   * tres el bloque no se reconocía. Dos renglones largos, paralelos y llenos de
   * '<' no se parecen a nada más que a un MRZ.
   */
  minimoDeTokens = 3
): OrientacionPorMrz | null {
  const candidatas = palabras.filter((w) => densidadDeMrz(w.text) > 0.5);
  if (candidatas.length < minimoDeTokens) return null;

  // Agrupar en renglones por su centro vertical.
  const orden = [...candidatas].sort((a, b) => a.bbox.y0 - b.bbox.y0);
  const renglones: PalabraOcr[][] = [];
  for (const w of orden) {
    const alto = Math.max(1, w.bbox.y1 - w.bbox.y0);
    const fila = renglones.find((r) => Math.abs(r[0].bbox.y0 - w.bbox.y0) < alto * 0.6);
    if (fila) fila.push(w);
    else renglones.push([w]);
  }

  // El CUARTO DE VUELTA sale de la forma de las cajas de los renglones.
  //
  // No de una nube de puntos: tesseract devuelve cada renglón del MRZ como UNA
  // sola palabra, así que los centros son tres puntos apilados en vertical y
  // cualquier ajuste da 90° cuando corresponde 0°. Un renglón de MRZ horizontal
  // tiene una caja mucho más ancha que alta; girado un cuarto de vuelta, al
  // revés. Eso es lo que se puede saber, y alcanza para orientar.
  //
  // El ángulo FINO —los pocos grados de inclinación— no sale de cajas alineadas
  // a los ejes: haría falta la caja de cada carácter. Se dice y no se inventa.
  const cajas = renglones.map((r) => ({
    x: Math.min(...r.map((w) => w.bbox.x0)),
    y: Math.min(...r.map((w) => w.bbox.y0)),
    w: Math.max(...r.map((w) => w.bbox.x1)) - Math.min(...r.map((w) => w.bbox.x0)),
    h: Math.max(...r.map((w) => w.bbox.y1)) - Math.min(...r.map((w) => w.bbox.y0)),
  }));

  const largos = cajas.filter((c) => Math.max(c.w, c.h) >= 8 * Math.min(c.w, c.h));
  if (largos.length === 0) return null;
  const horizontales = largos.filter((c) => c.w > c.h).length;
  const esHorizontal = horizontales >= largos.length / 2;
  const cuartosDeVuelta: 0 | 90 | 180 | 270 = esHorizontal ? 0 : 90;
  const anguloGrados = cuartosDeVuelta;

  // El MRZ ocupa casi todo el ancho de la tarjeta, así que el renglón más largo
  // da la escala. Es menos preciso que el código de barras —el OCR recorta
  // renglones— y por eso va aparte y puede ser null.
  const masLargo = largos.reduce((a, b) => (b.w > a.w ? b : a), largos[0]);
  const anchoTarjeta = masLargo.w / fraccionAnchoMrz;
  // El borde de arriba sale de dónde empiezan los renglones. Ponerlo en 0 era
  // suponer que la tarjeta arranca en el borde de la foto, y con eso el recorte
  // se llevaba mesa por arriba y cortaba el MRZ por abajo.
  //
  // MEDIDO sobre un dorso completo: el bloque del MRZ empieza a 0,59 del alto de
  // la tarjeta. Es la misma clase de dato que las zonas del catálogo.
  const altoTarjeta = anchoTarjeta / TARJETA_ID1.relacionDeLados;
  const arribaDelMrz = Math.min(...cajas.map((c) => c.y));
  const tarjeta =
    esHorizontal && anchoTarjeta > 0
      ? {
          x: masLargo.x - (anchoTarjeta - masLargo.w) / 2,
          y: arribaDelMrz - ARRANQUE_DEL_MRZ * altoTarjeta,
          w: anchoTarjeta,
          h: altoTarjeta,
        }
      : null;

  return { anguloGrados, cuartosDeVuelta, renglones: cajas, tarjeta };
}

// ---------------------------------------------------------------------------
// Ángulo fino, por las cajas de cada carácter
// ---------------------------------------------------------------------------

/** Una caja, que es lo único que se necesita de un símbolo del OCR. */
export interface CajaDeSimbolo {
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

/**
 * Encadena símbolos a lo largo de un renglón.
 *
 * Agrupar por "y" fijo NO sirve y se midió: una línea de MRZ inclinada 2° se
 * desplaza unos 30 px de punta a punta —más que la altura de un carácter— y la
 * agrupación la parte en pedazos, con lo cual el ángulo sale cualquier cosa.
 * Encadenar siguiendo la línea la sigue por inclinada que esté.
 */
export function renglonesDeSimbolos(simbolos: CajaDeSimbolo[]): CajaDeSimbolo[][] {
  const orden = [...simbolos].sort((a, b) => a.bbox.x0 - b.bbox.x0);
  const renglones: CajaDeSimbolo[][] = [];
  for (const s of orden) {
    const cy = (s.bbox.y0 + s.bbox.y1) / 2;
    const alto = Math.max(1, s.bbox.y1 - s.bbox.y0);
    const fila = renglones.find((r) => {
      const ult = r[r.length - 1];
      const cyUlt = (ult.bbox.y0 + ult.bbox.y1) / 2;
      const hueco = s.bbox.x0 - ult.bbox.x1;
      return Math.abs(cy - cyUlt) < alto * 0.7 && hueco > -alto && hueco < alto * 4;
    });
    if (fila) fila.push(s);
    else renglones.push([s]);
  }
  return renglones;
}

/**
 * Inclinación del documento, en grados, a partir de los caracteres del MRZ.
 *
 * Es la medida más precisa de rotación que tiene el documento, y viene gratis
 * con el OCR que ya corremos. Cada renglón da su propio ángulo por regresión
 * sobre los centros de sus caracteres, y se toma la mediana: con tres renglones
 * independientes, un renglón mal agrupado no arrastra el resultado.
 *
 * VERIFICADO al revés sobre una foto real: girándola a propósito −3°, 2°, 5° y
 * −7°, la medición recupera cada uno dentro de 0,2°.
 *
 * Sirve para la inclinación FINA, no para el cuarto de vuelta: pasados unos
 * grados el OCR deja de entregar caracteres y esto se queda sin materia prima.
 * El cuarto de vuelta lo dan la forma de las cajas de renglón o el código 2D.
 */
export function inclinacionPorSimbolos(
  simbolos: CajaDeSimbolo[],
  minimoPorRenglon = 10
): number | null {
  const angulos: number[] = [];
  for (const renglon of renglonesDeSimbolos(simbolos)) {
    if (renglon.length < minimoPorRenglon) continue;
    const n = renglon.length;
    const cx = (s: CajaDeSimbolo) => (s.bbox.x0 + s.bbox.x1) / 2;
    const cy = (s: CajaDeSimbolo) => (s.bbox.y0 + s.bbox.y1) / 2;
    const mx = renglon.reduce((a, s) => a + cx(s), 0) / n;
    const my = renglon.reduce((a, s) => a + cy(s), 0) / n;
    let sxy = 0;
    let sxx = 0;
    for (const s of renglon) {
      sxy += (cx(s) - mx) * (cy(s) - my);
      sxx += (cx(s) - mx) ** 2;
    }
    if (sxx === 0) continue;
    angulos.push((Math.atan2(sxy, sxx) * 180) / Math.PI);
  }
  if (angulos.length === 0) return null;
  angulos.sort((a, b) => a - b);
  return angulos[Math.floor(angulos.length / 2)];
}
